import { createSelector } from '@ngrx/store';

import { RegionType } from '@core/api';

import { getConfigState } from './main.selectors';

const OS_BASEMAP_VARIANTS = ['os_road', 'os_light', 'os_road_27700', 'os_light_27700', 'os_road_3857', 'os_light_3857'];

export const getBaseLayers = createSelector(getConfigState, ({ baseLayers }) => {
  return Array.from(baseLayers).sort((a, b) => sortBaseLayers(a.id) - sortBaseLayers(b.id));
});

export const getDataLayers = createSelector(getConfigState, ({ dataLayers }) => dataLayers);

export const getDataLayer = (id: string) => createSelector(getDataLayers, (dataLayers) => dataLayers?.find((layer) => layer.id === id));

export const getPrintLayers = createSelector(getConfigState, ({ printLayers }) => printLayers);

export const getInteractiveLayers = createSelector(getConfigState, ({ interactiveLayers }) => interactiveLayers);

export const getSectors = createSelector(getConfigState, ({ sectors }) => sectors);

export const getProjectTemplates = createSelector(getConfigState, ({ projectTemplates }) => projectTemplates);

export const getSector = (id: string) => createSelector(getSectors, (state) => state?.find((sector) => sector.id === id));

export const getOrganisationTypes = createSelector(getConfigState, ({ organisationTypes }) => organisationTypes);

export const getOrganisationType = (id: string) =>
  createSelector(getOrganisationTypes, (state) => state?.find((orgType) => orgType.id === id));

export const getOSVariants = createSelector(getConfigState, ({ baseLayers }) =>
  baseLayers.filter((layer) => OS_BASEMAP_VARIANTS.includes(layer.id))
);

export const getUserProjectTemplates = createSelector(getConfigState, ({ projectTemplates }) =>
  projectTemplates?.filter((template) => !template.boundary)
);

export const getNumberOfDataLayers = createSelector(getDataLayers, (state) => state?.length || 0);
export const getNumberOfInteractiveLayers = createSelector(getInteractiveLayers, (state) => state?.length || 0);

export const getSubscriptionsPrices = createSelector(getConfigState, ({ subscriptionsPrices }) => subscriptionsPrices);

export const getWorkflowDiscounts = createSelector(getConfigState, ({ subscriptionsPrices }) => subscriptionsPrices?.discounts.workflows);

export const getPrintDiscounts = createSelector(getConfigState, ({ subscriptionsPrices }) => subscriptionsPrices?.discounts.prints);

export const getRefereeCredit = createSelector(getConfigState, ({ subscriptionsPrices }) => subscriptionsPrices?.referrals.forReferee);

export const getPrintBorders = createSelector(getConfigState, ({ printBorders }) => printBorders);

export const getRegionsConfig = createSelector(getConfigState, ({ regions }) => regions);

export const getAllRegions = createSelector(getConfigState, ({ regions }) => regions?.regions);

export const getRegions = createSelector(getConfigState, ({ regions }) =>
  regions?.regions.filter((region) => region.type === RegionType.REGION)
);

export const getSubregions = createSelector(getConfigState, ({ regions }) =>
  regions?.regions.filter((region) => region.type === RegionType.SUBREGION)
);

export const getAvailableRegionIds = createSelector(getConfigState, ({ regions }) => regions?.availableRegionIds);

export const getDefaultRegion = createSelector(getRegions, (regions) => regions?.find((region) => region.default));

export const getImportMethods = createSelector(getConfigState, ({ importMethods }) => importMethods);

const sortBaseLayers = (id: string): number => {
  switch (id) {
    case 'osm':
      return 1;
    case 'os_road_27700':
      return 2;
    case 'os_light_27700':
      return 3;
    case 'os_leisure_27700':
      return 4;
    case 'historical_os_multiple_scales':
      return 5;
    case 'stadia_aerial':
      return 6;
    case 'stadia_aerial_labels':
      return 7;
    case 'getmapping_aerial':
      return 8;
    case 'getmapping_millennium':
      return 9;
    case 'lidar_1m_dtm_2020_27700':
      return 10;
    case 'blank':
      return 11;
    default:
      return 12;
  }
};
